import {
  ActionEditorDialogComponent,
  ActionEditorDialogData,
} from '../../actions/action-editor-dialog/action-editor-dialog.component';
import { CARE_PLAN_DIALOG_OPTIONS } from '../../care-plan-dialog.options';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';
import { Chip } from 'primeng/chip';
import { DialogService } from 'primeng/dynamicdialog';
import { CarePlanStore } from '../../care-plan.store';
import { CARE_PLAN_CLIENT_CONTEXT } from '../../care-plan-client-context.token';
import { CarePlanActionsStore } from '../../actions/actions.store';
import { OutcomesStore } from '../../outcomes/outcomes.store';
import { UnlinkedOutcomesListComponent } from '../../outcomes/unlinked-outcomes-list/unlinked-outcomes-list.component';
import { OpenActionsListComponent } from '../../actions/open-actions-list/open-actions-list.component';
import {
  CARE_PLAN_CASE_STATUS_LABEL,
  isClosedCaseStatus,
  CarePlanCaseStatus,
} from '../../care-plan.model';
import { CARE_PLAN_FIXTURE_TAXONOMY } from '../../care-plan-fixture-taxonomy.token';
import { CarePlanGroupBy } from '../case-grouping';
import {
  CarePlanCaseFilterChip,
  buildCaseFilterChips,
  isDefaultCaseFilters,
} from '../case-filters';
import { CaseGroupComponent } from '../case-group/case-group.component';

type CarePlanView = 'cases' | 'actions' | 'outcomes';
const CARE_PLAN_VIEWS: CarePlanView[] = ['cases', 'actions', 'outcomes'];
const VIEW_TAB_IDS: Record<CarePlanView, string> = {
  cases: 'bn-care-plan-cases-tab',
  actions: 'bn-care-plan-open-actions-tab',
  outcomes: 'bn-care-plan-unlinked-outcomes-tab',
};

interface GroupByOption {
  label: string;
  value: CarePlanGroupBy;
}

interface FilterOption<T extends string> {
  label: string;
  value: T;
}

const GROUP_BY_OPTIONS: GroupByOption[] = [
  { label: 'Status', value: 'status' },
  { label: 'Client goal', value: 'goal' },
  { label: 'Domain', value: 'domain' },
];

/**
 * Cases/Open actions toggle, case filters, and the selected client's actions.
 */
@Component({
  selector: 'bn-case-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CaseGroupComponent,
    OpenActionsListComponent,
    UnlinkedOutcomesListComponent,
    FormsModule,
    Select,
    Chip,
  ],
  providers: [CarePlanActionsStore, OutcomesStore, DialogService],
  templateUrl: './case-list.component.html',
  styleUrl: './case-list.component.scss',
})
export class CaseListComponent {
  protected readonly store = inject(CarePlanStore);
  protected readonly actionsStore = inject(CarePlanActionsStore);
  protected readonly outcomesStore = inject(OutcomesStore);
  private readonly _clientContext = inject(CARE_PLAN_CLIENT_CONTEXT, {
    optional: true,
  });
  private readonly _document = inject(DOCUMENT);
  private readonly _fixtureTaxonomy = inject(CARE_PLAN_FIXTURE_TAXONOMY, {
    optional: true,
  });
  private readonly dialog = inject(DialogService);
  readonly actionsLoading = this.actionsStore.loading;
  readonly addActionDisabledReason = computed(() => {
    if (this.store.isLive() && !this.store.canEditLiveCases()) {
      return 'You have view-only Care Plan access.';
    }
    if (!this._currentClientId())
      return 'Select a client before adding an action.';
    if (this.actionsStore.loading())
      return 'Loading actions and action reference data.';
    if (!this.actionsStore.canCreate())
      return 'Required action reference data is unavailable.';
    return null;
  });
  readonly canAddAction = computed(() => !this.addActionDisabledReason());

  openAddAction(): void {
    if (!this.canAddAction()) return;
    const clientId = this._currentClientId();
    const source = this.actionsStore;
    const editor: ActionEditorDialogData['editor'] = {
      analyticsContext: () => source.analyticsContext(),
      actionTypes: source.actionTypes,
      statuses: source.statuses,
      priorities: source.priorities,
      communicationPreferences: source.communicationPreferences,
      ownerLabel: (id) => source.ownerLabel(id),
      statusLabel: (code) => source.statusLabel(code),
      create: (value, options) => {
        if (clientId !== this._currentClientId() || !this.canAddAction()) {
          throw new Error(
            'Client or permissions changed. Close and reopen Add action.',
          );
        }
        if (
          value.caseId &&
          !this.store
            .cases()
            .some(
              (item) =>
                item.id === value.caseId && !isClosedCaseStatus(item.status),
            )
        ) {
          throw new Error(
            'The selected case is no longer available. Select another case or No case.',
          );
        }
        return source.create(value, options);
      },
    };
    const ref = this.dialog.open(ActionEditorDialogComponent, {
      ...CARE_PLAN_DIALOG_OPTIONS,
      width: '42rem',
      breakpoints: { '768px': 'calc(100vw - 2rem)' },
      styleClass: 'bn-action-editor-dialog-host',
      data: {
        mode: 'add',
        entryPoint: 'care_plan_client',
        editor,
        caseOptions: [
          { label: 'No case', value: null },
          ...this.store
            .cases()
            .filter((item) => !isClosedCaseStatus(item.status))
            .map((item) => ({
              label: `${item.title} (#${item.id})`,
              value: item.id,
            })),
        ],
      } satisfies ActionEditorDialogData,
    });
    ref?.onClose.subscribe((created) => {
      if (created && clientId === this._currentClientId())
        this.activeView.set('actions');
    });
  }

  protected readonly activeView = signal<CarePlanView>('cases');

  constructor() {
    effect(() => {
      const clientId = this._currentClientId();
      this.store.activeGuid();
      this.activeView.set('cases');
      untracked(() => void this.outcomesStore.bind(null, this.store));
      if (clientId) {
        void this.actionsStore.loadOpenForClient(clientId);
      } else {
        this.actionsStore.clear();
      }
    });
  }

  protected readonly groupByOptions = computed(() =>
    !this.store.goalsEnabled() ||
    (this.store.isLive() &&
      (!this.store.goalsLoaded() || !!this.store.goalsError()))
      ? GROUP_BY_OPTIONS.filter((option) => option.value !== 'goal')
      : GROUP_BY_OPTIONS,
  );

  protected readonly isDefaultFilters = computed(() =>
    isDefaultCaseFilters(this.store.filters()),
  );

  protected readonly statusOptions = computed<FilterOption<string>[]>(() => [
    { label: 'All active statuses', value: '' },
    ...(this.store.isLive()
      ? (this.store.caseStatusReference()?.CASE_STATUS ?? []).map((item) => ({
          label: item.value,
          value: item.code.toLowerCase(),
        }))
      : Object.entries(CARE_PLAN_CASE_STATUS_LABEL).map(([value, label]) => ({
          value,
          label,
        }))
    ).filter((item) => item.value !== 'suggested'),
  ]);

  protected readonly domainOptions = computed<FilterOption<string>[]>(() => [
    { label: 'All domains', value: '' },
    ...(this.store.isLive()
      ? this.store.domains().map((domain) => ({
          label: domain.shortName || domain.name,
          value: `domain-id:${domain.domainId}`,
        }))
      : (this._fixtureTaxonomy?.domains ?? []).map((domain) => ({
          label: domain.label,
          value: domain.key,
        }))),
  ]);

  protected readonly goalOptions = computed<FilterOption<string>[]>(() => [
    { label: 'All client goals', value: '' },
    ...this.store.activeGoals().map((goal) => ({
      label: goal.displayText,
      value: goal.id,
    })),
  ]);

  protected readonly chips = computed<CarePlanCaseFilterChip[]>(() =>
    buildCaseFilterChips(this.store.filters(), {
      statusLabel: (status) => this.store.statusLabel(status),
      domainLabel: (key) =>
        this._fixtureTaxonomy?.domains.find((domain) => domain.key === key)
          ?.label ??
        this.store
          .domains()
          .find((domain) => `domain-id:${domain.domainId}` === key)?.name ??
        key,
      goalLabel: (goalId) =>
        this.store.goals().find((g) => g.id === goalId)?.displayText ?? goalId,
    }),
  );

  onGroupByChange(groupBy: CarePlanGroupBy): void {
    this.store.setGroupBy(groupBy);
  }

  onStatusFilterChange(status: CarePlanCaseStatus | ''): void {
    this.store.setFilters({ status: status || null });
  }

  onDomainFilterChange(domain: string): void {
    this.store.setFilters({ domain: domain || null });
  }

  onGoalFilterChange(goalId: string): void {
    this.store.setFilters({
      goalId: this.store.goalsEnabled() ? goalId || null : null,
    });
  }

  clearChip(dimension: CarePlanCaseFilterChip['dimension']): void {
    if (dimension === 'status') this.store.setFilters({ status: null });
    if (dimension === 'domain') this.store.setFilters({ domain: null });
    if (dimension === 'goal') this.store.setFilters({ goalId: null });
  }

  resetFilters(): void {
    this.store.resetFilters();
  }

  selectView(view: CarePlanView): void {
    this.activeView.set(view);
    if (view === 'outcomes') void this.outcomesStore.load();
    if (view === 'actions') {
      const clientId = this._currentClientId();
      if (clientId) void this.actionsStore.loadOpenForClient(clientId);
    }
  }

  onViewTabKeydown(event: KeyboardEvent, currentView: CarePlanView): void {
    const index = CARE_PLAN_VIEWS.indexOf(currentView);
    let nextView: CarePlanView | null = null;
    if (event.key === 'ArrowRight')
      nextView = CARE_PLAN_VIEWS[(index + 1) % CARE_PLAN_VIEWS.length];
    else if (event.key === 'ArrowLeft')
      nextView =
        CARE_PLAN_VIEWS[
          (index + CARE_PLAN_VIEWS.length - 1) % CARE_PLAN_VIEWS.length
        ];
    else if (event.key === 'Home') nextView = 'cases';
    else if (event.key === 'End') nextView = 'outcomes';
    if (!nextView) return;
    event.preventDefault();
    this.selectView(nextView);
    const tabId = VIEW_TAB_IDS[nextView];
    queueMicrotask(() => this._document.getElementById(tabId)?.focus());
  }

  goToCase(caseId: string): void {
    if (!this.store.cases().some((item) => item.id === caseId)) return;
    this.store.resetFilters();
    this.store.setExpandedCase(caseId);
    this.activeView.set('cases');
    setTimeout(() => {
      this._document
        .getElementById(`bn-case-card-${caseId}`)
        ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    });
  }

  private _currentClientId(): number | null {
    const sessionGuid = this.store.sessionGuid();
    const metadataGuid = this._clientContext?.activeGuid() ?? null;
    const clientId = this._clientContext?.clientId() ?? null;
    return sessionGuid &&
      metadataGuid === sessionGuid &&
      clientId &&
      Number.isSafeInteger(clientId)
      ? clientId
      : null;
  }
}
